import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, AbstractControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';

import { Config } from 'src/app/app-constant';
import { HttpService } from 'src/app/service/http/http.service';
import { EncryptionService } from 'src/app/service/encryption/encryption.service';
import { SharedService } from 'src/app/service/shared/shared.service';
import { DataRefreshService } from 'src/app/services/dataRefreshService.service';
import { HttpClient } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { switchMap, map, catchError, tap } from 'rxjs/operators';

interface RatingDefinition {
  ratingValue: number;
  label: string;
  isDeleted: boolean;
}

interface Question {
  text: string;
  selectedRating?: number;
  comment?: string;
}

interface Competency {
  candidateName?: string;
  competencyName: string;
  questions: Question[];
}

@Component({
  selector: 'app-evaluation-form',
  templateUrl: './evaluation-form.component.html',
  styleUrls: ['./evaluation-form.component.scss'],
})
export class EvaluationFormComponent implements OnInit {
  evaluationForm!: FormGroup;
  ratingDefinitions: RatingDefinition[] = [];
  buttonColorName: string | null = null;
  subModuleButtonColor: string | null = null;
  loading: boolean = false;
  loggedInUserCompanyID: string = '';
  fiscalYear: string = '';
  month: number = 0;
  candidateId: number = 0;
  candidateName: string = '';
  interviewRound: number = 0;
  loggedInUserName: string = '';
  loggedInUserId: number = 0;
  isRoundDeleted: boolean = false;
  isPositionClosed: boolean = false;
  isEvaluatorUnauthorized: boolean = false;
  interviewId: number = 0;
  isAlreadyEvaluated = false;
  evaluatedByName: string = ''; 
  finalStatus:any;
  maxCommentLength = 250; // now accessible in template

  constructor(
    private fb: FormBuilder,
    private httpService: HttpService,
    private config: Config,
    private encryptionService: EncryptionService,
    private dataRefreshService: DataRefreshService,
    public dialog: MatDialog,
    public sharedService: SharedService,
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
  ) { }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      this.loggedInUserCompanyID = params.get('companyId') || '';
      this.fiscalYear = params.get('fiscalYear') || '';
      this.month = +(params.get('month') || 0);
      this.candidateId = +(params.get('candidateId') || 0);
      this.candidateName = params.get('candidateName') || '';
      this.interviewRound = +(params.get('interviewRound') || 0);
      this.interviewId = +(params.get('interviewId') || 0);
      this.loggedInUserName = params.get('evaluatedBy') || '';
      this.loggedInUserId = +(params.get('evaluatedById') || 0);

      history.replaceState({}, '', this.router.url.split('?')[0]);

      this.initForm();
      this.getCandidateCompetencyData();
    });
  }

  private initForm(): void {
    this.evaluationForm = this.fb.group({
      competencies: this.fb.array([]),
      techPositions: this.fb.array([])
    });
  }
  private getCandidateCompetencyData(): void {
    const url = `${this.config.getEvaluationCandidate}?companyId=${this.loggedInUserCompanyID}&fiscalYear=${this.fiscalYear}&month=${this.month}&candidateId=${this.candidateId}&InterviewRound=${this.interviewRound}&interviewId=${this.interviewId}`;

    this.http.get<any>(url).subscribe({
      next: (res: any) => {
        const data = res?.[0];
        if (!data) {
          this.toastr.warning('Interview round data not found.');
          this.isRoundDeleted = true;
          return;
        }
        if (Number(data.isDeleted) === 1) {
          this.toastr.error('This interview round has been deleted.');
          this.isRoundDeleted = true;
          return;
        }
        if (data.positionClosingDate && new Date(data.positionClosingDate) < new Date()) {
          this.toastr.warning('⚠️ Position is already closed. Evaluation is not allowed.');
          this.evaluationForm.disable();
          this.isPositionClosed = true;
          return;
        }
        if (data.finalStatus === 'Hired' || data.finalStatus === 'Rejected') {
          this.finalStatus = data.finalStatus;
        
          // Disable evaluation form completely
          if (this.evaluationForm) {
            this.evaluationForm.disable();
          }
        
          // Show appropriate toast (optional, since UI already shows message)
          this.toastr.info(
            data.finalStatus === 'Hired'
              ? '✅ Candidate is already Hired. Evaluation is locked.'
              : '❌ Candidate is already Rejected. Evaluation is not allowed.'
          );
        
          return; // stop further checks (highest priority)
        }
        
        // ✅ Check if evaluator is in leadInterviewer object
        const leadInterviewerMap = data.leadInterviewer || {};
        const evaluatorIdStr = String(this.loggedInUserId);

        if (!leadInterviewerMap.hasOwnProperty(evaluatorIdStr)) {
          this.toastr.error('You are not authorized to evaluate this candidate.');
          this.isEvaluatorUnauthorized = true;
          return;
        }
        // ✅ Extract interviewId for future update
        this.interviewId = data.interviewId || 0;
        this.patchCompetencyForm(data); // helper method
        this.checkIfAlreadyEvaluated();
      },
      error: (err) => {
        // console.error('Error fetching candidate competency data:', err);
        this.toastr.error('Failed to load evaluation data.');
      }
    });
  }
  private patchCompetencyForm(data: any): void { 
    this.evaluationForm = this.fb.group({
      competencies: this.fb.array(
        data.competencies.map((comp: any) =>
          this.fb.group({
            competenciesId: [comp.competenciesId],
            competencyName: [comp.competency],
            questions: this.fb.array(
              comp.questions.map((q: any) => {
                if (!this.ratingDefinitions.length) {
                  this.ratingDefinitions = q.ratings.filter((r: any) => !r.isDeleted);
                }

                return this.fb.group({
                  questionId: [q.questionsId],
                  text: [q.question],
                  selectedRating: [null, Validators.required],
                  comment: ['', Validators.maxLength(this.maxCommentLength)]  
                });
              })
            )
          })
        )
      ),
      // ✅ build techPositions
      techPositions: this.fb.array(
        (data.techPositions || []).map((tp: any) =>
          this.fb.group({
            techPosition: [tp.techPosition],
            questions: this.fb.array(
              tp.questions.map((q: any) => {
                const isRatingBased = q.questionType === 'Rating based';
                return this.fb.group({
                  id: [q.id],
                  questionType: [q.questionType],
                  questions: [q.questions],
                  selectedRating: [isRatingBased ? null : undefined, isRatingBased ? Validators.required : []],
                  comment: ['', Validators.maxLength(this.maxCommentLength)],
                  ratings: [q.ratings || []]
                });
              })
            )
          })
        )
      )
    });
  }


  get competenciesForm(): FormArray {
    return this.evaluationForm.get('competencies') as FormArray;
  }
  get techPositionsForm(): FormArray {
    return this.evaluationForm.get('techPositions') as FormArray;
  }
 
  getQuestions(compIndex: number): FormArray {
    return this.competenciesForm.at(compIndex).get('questions') as FormArray;
  }
  
  trackByIndex(index: number): number {
    return index;
  }

  // trackByRating(index: number, item: RatingDefinition): number {
  //   return item.ratingValue;
  // }
  trackByRating(index: number, item: any): number {
    return item?.ratingValue ?? index;
  }
  
  onClickBack(): void {
    this.router
      .navigate(['/home/hrportal/recruitment/evaluation'], {
        queryParamsHandling: 'merge',
      })
      .then(() => {
        this.router.onSameUrlNavigation = 'reload';
      });
  }

  onSubmit(): void {
    if (this.evaluationForm.invalid) {
      this.toastr.warning('Please fill all required fields');
      return;
    }
    const rawValue = this.evaluationForm.getRawValue();
    const compPayload = rawValue.competencies.flatMap((comp: any, compIndex: number) =>
      comp.questions.map((q: any, qIndex: number) => {
        const ratingObj = this.ratingDefinitions.find(
          (r: any) => r.ratingValue === q.selectedRating
        );

        return {
          companyId: this.loggedInUserCompanyID,
          fiscalYear: this.fiscalYear,
          month: this.month,
          candidateId: this.candidateId,
          candidateName: this.candidateName,
          interviewRound: this.interviewRound,
          evaluatedBy: this.loggedInUserName,
          evaluatedById: this.loggedInUserId,
          competencyName: comp.competencyName,
          question: q.text,
          rating: ratingObj?.label || '',
          comments: q.comment
        };
      })
    );
 
  // ================= TECH POSITIONS PAYLOAD =================
  const tpPayload = rawValue.techPositions.flatMap((tp: any) =>
    tp.questions.map((q: any) => {
      let ratingLabel = '';
      if (q.questionType === 'Rating based' && q.ratings) {
        const found = q.ratings.find((r: any) => r.ratingValue === q.selectedRating);
        ratingLabel = found?.label || '';
      }

      return {
        companyId: this.loggedInUserCompanyID,
        fiscalYear: this.fiscalYear,
        month: this.month,
        candidateId: this.candidateId,
        candidateName: this.candidateName,
        interviewRound: this.interviewRound,
        evaluatedBy: this.loggedInUserName,
        evaluatedById: this.loggedInUserId,
        techPosition: tp.techPosition,   // ✅ parent-level info
        questionType: q.questionType,    // ✅ from question
        question: q.questions,
        rating: ratingLabel,
        comments: q.comment
      };
    })
  );

    const finalPayload = [...compPayload, ...tpPayload];


    const url = this.config.submitEvaluationData;
    this.http.post(url, finalPayload).subscribe({
      next: () => {
        this.toastr.success('Evaluation submitted successfully');
        this.evaluationForm.disable();
        this.isAlreadyEvaluated = true;
        this.evaluatedByName = this.loggedInUserName;
        // ✅ add interview finalStatus to 'Evaluated'
        this.addEvaluatedInterviewEntry();
      },
      error: (err) => {
        // console.error('Submission error:', err);
        this.toastr.error('Something went wrong while submitting.');
      }
    });
  }
  isRatingBased(group: AbstractControl): boolean {
    return group.get('questionType')?.value === 'Rating based';
  }
  isOpenEnded(group: AbstractControl): boolean {
    return group.get('questionType')?.value === 'Open Ended';
  }
  trackById(_i: number, ctrl: AbstractControl) {
    return ctrl.value?.id || ctrl.value?.questionsId || _i;
  }
  getTechQuestions(tpIndex: number): FormArray {
    return this.techPositionsForm.at(tpIndex).get('questions') as FormArray;
  }
  
  private addEvaluatedInterviewEntry(): void {
    const fetchInterviewUrl = `${this.config.getInterviewByIdAndRound}?candidateId=${this.candidateId}&interviewRound=${this.interviewRound}&interviewId=${this.interviewId}`;
    const fetchEvaluationUrl = `${this.config.getEvaluationDetails}?candidateId=${this.candidateId}&interviewRound=${this.interviewRound}&interviewId=${this.interviewId}`;
  
    forkJoin({
      interviews: this.http.get<any>(fetchInterviewUrl),
      evaluations: this.http.get<any>(fetchEvaluationUrl)
    })
      .pipe(
       
        map(({ interviews, evaluations }) => {
          const interviewList = interviews?.data || [];
          if (!interviewList.length) throw new Error('No interview records found for candidate.');
        
          const latest = { ...interviewList[interviewList.length - 1] };
        
          const leadIds: string[] = Object.keys(latest.leadInterviewer || {}).map(id => String(id));
          if (!leadIds.length) throw new Error('No lead interviewers found.');
        
          // ✅ FIX: Normalize evaluations whether raw array or wrapped
          const evaluationList: any[] = Array.isArray(evaluations)
            ? evaluations
            : (evaluations?.data || []);
        
          const evaluatedLeadIds = new Set(
            evaluationList.map((e: any) => String(e.evaluatedById))
          );
        
        
          const allLeadsEvaluated = leadIds.every(id => evaluatedLeadIds.has(id));
          if (!allLeadsEvaluated) throw new Error('Not all lead interviewers have submitted evaluations.');
        
          return {
            ...latest,
            finalStatus: 'Evaluated',
            isDeleted: 0,
            createdAt: undefined,
            updatedAt: undefined,
            id: undefined
          };
        }),
        
        switchMap(payload => this.http.post(this.config.addAuthInterviewDetails, [payload])),
        catchError(err => {
          // console.error('❌ Error in addEvaluatedInterviewEntry:', err.message || err);
          return [];
        })
      )
      .subscribe();
  }
  

  private getRatingValueFromLabel(label: string): string | null {
    const found = this.ratingDefinitions.find(r => r.label === label);
    return found ? String(found.ratingValue) : null;
  }  
  private checkIfAlreadyEvaluated(): void {
    const url = `${this.config.getEvaluationDetails}?candidateId=${this.candidateId}&interviewRound=${this.interviewRound}&interviewId=${this.interviewId}`;
  
    this.http.get<any[]>(url).subscribe({
      next: (res) => {
        const evaluatorData = res?.filter(item => item.evaluatedById === this.loggedInUserId) || [];
  
        if (evaluatorData.length) {
          this.evaluationForm.enable(); // keep form editable by default
  
          evaluatorData.forEach((item: any) => {
            /** ================= COMPETENCIES ================= */
            const compIndex = this.competenciesForm.controls.findIndex(
              (c: any) =>
                c.get('competencyName')?.value?.trim().toLowerCase() ===
                item.competencyName?.trim().toLowerCase()
            );
  
            if (compIndex > -1) {
              const questions = this.getQuestions(compIndex);
  
              questions.controls.forEach((qCtrl: AbstractControl) => {
                if (
                  qCtrl.get('text')?.value?.trim().toLowerCase() ===
                  item.question?.trim().toLowerCase()
                ) {
                  const ratingValue = this.getRatingValueFromLabel(item.rating);
  
                  qCtrl.patchValue({
                    selectedRating: ratingValue,
                    comment: item.comments
                  });
  
                  qCtrl.disable(); // 🔒 lock only this answered question
                }
              });
            }
  
            /** ================= TECH POSITIONS ================= */
            this.techPositionsForm.controls.forEach((tp: AbstractControl) => {
              const questions = tp.get('questions') as FormArray;
  
              questions.controls.forEach((qCtrl: AbstractControl) => {
                if (
                  qCtrl.get('questions')?.value?.trim().toLowerCase() ===
                  item.question?.trim().toLowerCase()
                ) {
                  if (item.rating) {
                    const ratingValue = this.getRatingValueFromLabel(item.rating);
                    qCtrl.patchValue({ selectedRating: ratingValue });
                  }
                  if (item.comments) {
                    qCtrl.patchValue({ comment: item.comments });
                  }
  
                  qCtrl.disable(); // 🔒 lock only this answered tech question
                }
              });
            });
          });
  
          // ❌ removed this.evaluationForm.disable();
          // ✅ mark evaluator info
          this.isAlreadyEvaluated = true;
          this.evaluatedByName = evaluatorData[0]?.evaluatedBy;
        }
      },
      error: err => {
        // console.error('Error checking evaluation history:', err);
      }
    });
  }
  
}
